import { FixTypeLater } from 'react-redux'
import { put, takeLatest, call, select } from 'redux-saga/effects'
import { sagaActions } from '../sagaActions'
import { setPlayerToken } from '@/redux/reducers/userReducer'
import { callPublicAPI } from '.'
import {
  ACCESS_TOKEN_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
  USER_INFO_STORAGE_KEY,
  TOKEN_BALANCE_FRESH_STORAGE_KEY,
  TOKEN_BALANCE_FRESH_TRUE,
} from '@/constants/storage'
import { initializeLanguage } from '@/i18n'
import {
  clearThemeFromStorage,
  DEFAULT_THEME,
  getThemeFromStorage,
  parseTheme,
  setThemeToStorage,
  updateDocumentBodyByTheme,
} from '@/ui-components/utils/theme-utils'
import {
  clearLanguageFromStorage,
  getLanguageFromStorage,
} from '@/i18n/storage'
import { DEFAULT_LANGUAGE } from '@/i18n/constants'
import { DEFAULT_CURRENCY } from '@/ui-components/utils/currency-config'
import { Theme } from '@/ui-components/constants/theme'
import { changeLanguage } from '@/i18n/action'

const API_EXCHANGE_TOKEN = `${import.meta.env.VITE_HOST}/api/v1/seamless/exchange_token`
const getQueryString = (state: FixTypeLater) => {
  const search = state.router?.location?.search || window.location.search
  return new URLSearchParams(search)
}

const getRoundId = (state: FixTypeLater) => {
  const urlParams = getQueryString(state)
  return urlParams.get('roundId')
}

const getGameId = (state: FixTypeLater) => {
  const urlParams = getQueryString(state)
  return urlParams.get('gameId')
}

// Decode a JWT (without verifying) and return its JSON payload as an object
const parseJwt = (jwt: string) => {
  const base64Url = jwt?.split?.('.')?.[1]
  if (!base64Url) return {}
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  )
  try {
    return JSON.parse(String(jsonPayload))
  } catch {
    return {}
  }
}

// Centralized helper to set player token/state and initialize language
const selectUserBalance = (state: FixTypeLater) => state?.user?.userBalance

function* applyPlayerState(
  accessToken: string,
  token: string,
  userInfo: FixTypeLater,
  includeBalanceFromToken: boolean,
): FixTypeLater {
  const tokenInfo = parseJwt(token)
  const currentBalance: number | undefined = yield select(selectUserBalance)

  const payload: FixTypeLater = {
    accessToken: accessToken?.toString(),
    token: token?.toString(),
    userName: userInfo?.unique_username,
    userId: userInfo?.user_id,
    providerId: userInfo?.provider_id,
    ipAddeess: userInfo?.ip_address,
    logoUrl: tokenInfo?.logo_url,
    logoUrlDark: tokenInfo?.logo_url_dark,
    logoLinkUrl: tokenInfo?.logo_link_url,
    faviconUrl: tokenInfo?.favicon_url,
    title: tokenInfo?.title,
    theme: parseTheme(tokenInfo?.theme ?? DEFAULT_THEME),
    provider: {
      defaultLanguage: tokenInfo?.provider?.defaultLanguage ?? DEFAULT_LANGUAGE,
      languages: tokenInfo?.provider?.languages,
      currency: tokenInfo?.provider?.currency ?? { ...DEFAULT_CURRENCY },
    },
  }

  if (payload.provider?.currency) {
    if ([Theme.BLUE_LIGHT, Theme.BLUE_DARK].includes(payload.theme as Theme)) {
      payload.provider.currency.position = 'after'
    }
  }

  // Only set userBalance when instructed AND current state is undefined
  if (
    includeBalanceFromToken &&
    currentBalance === undefined &&
    tokenInfo?.user_balance !== undefined
  ) {
    payload.userBalance = tokenInfo.user_balance
  }

  yield put(setPlayerToken(payload))

  // Initialize language only if user has no stored preference
  // This prevents overriding user-selected language on navigation
  const storedLang = getLanguageFromStorage()
  if (!storedLang) {
    yield call(
      initializeLanguage,
      tokenInfo?.provider?.defaultLanguage ?? DEFAULT_LANGUAGE,
      tokenInfo?.provider?.languages,
    )
  } else {
    yield call(changeLanguage, storedLang)
  }

  // Initialize theme only if user has no stored preference
  const storedTheme = getThemeFromStorage()
  if (!storedTheme) {
    updateDocumentBodyByTheme(payload.theme)
    setThemeToStorage(payload.theme)
  }

  return null
}

function* initializeToServer(action: FixTypeLater): FixTypeLater {
  const roundId = yield select(getRoundId)
  const gameId = yield select(getGameId)
  const { token } = action.payload

  const data: {
    token: string
    roundId?: number | null
    gameId?: number | null
  } = {
    token,
  }

  if (roundId) {
    data.roundId = Number(roundId)
  }

  if (gameId) {
    data.gameId = Number(gameId)
  }

  try {
    const result: FixTypeLater = yield call(callPublicAPI, {
      url: API_EXCHANGE_TOKEN,
      method: 'POST',
      data,
    })

    if (result.status !== 200) {
      throw new Error('Could not initilize to server')
    }

    yield put({
      type: sagaActions.INITIALIZED_STATE_TO_SERVER,
      payload: {
        resultExchangeToken: result.data,
        token: token,
      },
    })
  } catch {
    yield put({
      type: sagaActions.ROUTER_NAVIGATE_REQUEST,
      payload: { redirectToPath: '/session_error', delay: 1500 },
    })
  }
}

function* initializedToServer(action: FixTypeLater): FixTypeLater {
  const jwt = action.payload.resultExchangeToken.access_token // replace with your JWT
  const base64Url = jwt.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join(''),
  )

  yield call(() => {
    localStorage.setItem(
      ACCESS_TOKEN_STORAGE_KEY,
      action.payload.resultExchangeToken.access_token,
    )
    localStorage.setItem(TOKEN_STORAGE_KEY, action.payload.token)
    localStorage.setItem(USER_INFO_STORAGE_KEY, jsonPayload)
    localStorage.setItem(
      TOKEN_BALANCE_FRESH_STORAGE_KEY,
      TOKEN_BALANCE_FRESH_TRUE,
    )
  })
  clearLanguageFromStorage()
  clearThemeFromStorage()
  const userInfo = JSON.parse(String(jsonPayload))
  yield* applyPlayerState(
    action.payload.resultExchangeToken.access_token,
    action.payload.token,
    userInfo,
    true,
  )
  yield put({
    type: sagaActions.ROUTER_NAVIGATE_REQUEST,
    payload: {
      redirectToPath: action.payload.resultExchangeToken.redirect_link,
      delay: 0,
    },
  })
}

function* initializeApp(action: FixTypeLater): FixTypeLater {
  const pathname = action.payload.location.pathname
  const pathnames = pathname.split('/')

  if (
    !pathnames.includes('startGame') &&
    !pathnames.includes('ticket_transactions')
  ) {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)
    const userInfoStr: string | null = localStorage.getItem(
      USER_INFO_STORAGE_KEY,
    )
    const userInfo = JSON.parse(String(userInfoStr))

    if (!accessToken || !token || !userInfoStr) {
      yield put({
        type: sagaActions.ROUTER_NAVIGATE_REQUEST,
        payload: { redirectToPath: '/session_error', delay: 0 },
      })
    } else {
      const shouldUseTokenBalance =
        localStorage.getItem(TOKEN_BALANCE_FRESH_STORAGE_KEY) ===
        TOKEN_BALANCE_FRESH_TRUE
      yield* applyPlayerState(
        accessToken,
        token,
        userInfo,
        shouldUseTokenBalance,
      )
    }
  } else {
    // yield put({
    //   type: sagaActions.INITIALIZE_STATE_TO_SERVER,
    //   payload: { redirectToPath: '/', delay: 0 }
    // })
  }
}

export function* initialize() {
  yield takeLatest<FixTypeLater>(
    sagaActions.INITIALIZE_STATE_TO_SERVER,
    initializeToServer,
  )

  yield takeLatest<FixTypeLater>(
    sagaActions.INITIALIZED_STATE_TO_SERVER,
    initializedToServer,
  )

  yield takeLatest<FixTypeLater>(sagaActions.INITIALIZE_APP, initializeApp)
}
